import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { News } from '../domain/news';

export interface NewsSchema {
  title?: string | null;
  subtitle: string;
  content: string;
  date: Timestamp;
  action?: string | null;
}

@Injectable()
export class NewsRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<NewsSchema>('news', queryFn);
  }

  toSchema(news: News): NewsSchema {
    return <NewsSchema>{
      title: news.title ?? null,
      subtitle: news.subtitle ?? null,
      content: news.content,
      date: Timestamp.fromDate(news.date),
      action: news.action ?? null,
    };
  }

  fromSchema(schema: NewsSchema, id: string): News {
    return News.create(
      {
        title: schema.title ?? 'Nouveautés',
        subtitle: schema.subtitle,
        content: schema.content,
        date: schema.date.toDate(),
        action: schema.action ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  async findAll(lastId: string | undefined): Promise<News[]> {
    if (lastId) {
      const last = await firstValueFrom(this.collection().doc(lastId).get());
      const snap = await firstValueFrom(
        this.collection((ref) => {
          return ref.orderBy('date', 'desc').startAfter(last).limit(30);
        }).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    } else {
      const snap = await firstValueFrom(
        this.collection((ref) => {
          return ref.orderBy('date', 'desc').limit(30);
        }).get(),
      );
      return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
    }
  }

  async findLastTwoNews(): Promise<News[]> {
    const snap = await firstValueFrom(
      this.collection((ref) => ref.orderBy('date', 'desc').limit(2)).get(),
    );
    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async findAllAfterDate(after: Date): Promise<News[]> {
    const snap = await firstValueFrom(
      this.collection((ref) =>
        ref.where('date', '>=', after).orderBy('date', 'desc'),
      ).get(),
    );
    return snap.docs.map((doc) => this.fromSchema(doc.data(), doc.id));
  }

  async create(news: News): Promise<News> {
    const schema = this.toSchema(news);
    const ref = await this.collection().add(schema);
    return this.fromSchema(schema, ref.id);
  }

  async save(news: News): Promise<News> {
    const schema = this.toSchema(news);
    await this.collection().doc(news.newsId.id.toString()).set(schema);
    return this.fromSchema(schema, news.newsId.id.toString());
  }

  async delete(newsId: string): Promise<void> {
    return this.collection().doc(newsId).delete();
  }
}
